/**
 * Keep on-canvas measurement labels readable when the user zooms out.
 *
 * Measurement labels (edge/segment distances drawn while sketching the garden
 * outline, a polygon or a curve) are Fabric Text objects sized in world
 * coordinates, so their on-screen size is `fontSize * zoom`. Zoomed out far
 * enough they become unreadable. This helper counter-scales them so they keep a
 * constant on-screen size once zoomed out, and hides them entirely below a
 * threshold to avoid overlapping clutter. When zoomed in (zoom >= 1) the labels
 * are left untouched and grow with the content, as before.
 *
 * Tag labels via `data.isMeasurementLabel = true`. Labels whose visibility is
 * owned by their tool (e.g. a preview label toggled while drawing) should also
 * set `data.ownVisibility = true` so this helper only scales them.
 */

// Below this zoom the labels are hidden (too much overlap to stay useful).
export const MEASUREMENT_HIDE_ZOOM = 0.3;

/**
 * Scale factor that keeps a world-space label at a constant on-screen size when
 * zoomed out, while leaving it untouched when zoomed in (>= 1).
 *
 * @param {number} zoom - Current canvas zoom (viewportTransform scale)
 * @returns {number}
 */
export function measurementLabelScale(zoom) {
  return zoom < 1 ? 1 / zoom : 1;
}

/**
 * Attach constant-on-screen-size behavior for measurement labels to a Fabric
 * canvas. Runs on every `before:render`, so it tracks every zoom entry point
 * (wheel, buttons, pinch, reset) and any newly added labels without needing to
 * wrap those code paths. Mutating scale inside `before:render` is picked up by
 * the in-progress render, so it triggers no extra render (no loop).
 *
 * @param {import('fabric').Canvas} canvas
 * @returns {() => void} unsubscribe
 */
export function attachMeasurementLabelScaling(canvas) {
  if (!canvas) return () => {};

  const update = () => {
    const zoom = canvas.getZoom();
    const scale = measurementLabelScale(zoom);
    const visible = zoom >= MEASUREMENT_HIDE_ZOOM;
    const objects = canvas.getObjects();
    for (let i = 0; i < objects.length; i++) {
      const o = objects[i];
      if (!o.data?.isMeasurementLabel) continue;
      o.scaleX = scale;
      o.scaleY = scale;
      // Tools that own a label's visibility (e.g. a drawing preview) opt out of
      // the hide behavior so we don't fight their show/hide toggling.
      if (!o.data.ownVisibility) {
        o.visible = visible;
      }
    }
  };

  canvas.on('before:render', update);
  return () => canvas.off('before:render', update);
}
